import React, { useState, useEffect, useRef } from 'react';

const HomePage = () => {
  const [isHeroTextVisible, setIsHeroTextVisible] = useState(true);
  const [player, setPlayer] = useState(null);
  const [flippedCards, setFlippedCards] = useState(new Set());
  const [currentHistorySlide, setCurrentHistorySlide] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);
  const timerRef = useRef(null);
  const playerRef = useRef(null);
  const lastTimeRef = useRef(0);
  const checkIntervalRef = useRef(null);
  const videoResetIntervalRef = useRef(null);
  const heroSectionRef = useRef(null);
  const intersectionObserverRef = useRef(null);
  const heroTextRef = useRef(null);
  
  const startFadeTimer = () => {
    // Clear any existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    
    // Set timer to fade out the hero text after 9 seconds
    timerRef.current = setTimeout(() => {
      setIsHeroTextVisible(false);
    }, 9000);
  };
  
  const resetHeroText = () => {
    setIsHeroTextVisible(true);
    startFadeTimer();
  };
  
  const handleCardClick = (cardIndex, event) => {
    // Don't flip if clicking on a button or link
    if (event.target.classList.contains('card-button') || 
        event.target.tagName === 'A' || 
        event.target.closest('.card-button')) {
      return;
    }
    
    // Prevent default behavior for card flipping
    event.preventDefault();
    event.stopPropagation();
    
    setFlippedCards(prev => {
      const newFlipped = new Set(prev);
      if (newFlipped.has(cardIndex)) {
        newFlipped.delete(cardIndex);
      } else {
        newFlipped.add(cardIndex);
      }
      return newFlipped;
    });
  };

  const handleCardTouch = (cardIndex, event) => {
    // Don't flip if touching a button or link
    if (event.target.classList.contains('card-button') || 
        event.target.tagName === 'A' || 
        event.target.closest('.card-button')) {
      return;
    }
    
    // Handle touch events specifically
    event.preventDefault();
    handleCardClick(cardIndex, event);
  };

  const historySlides = [
    {
      year: "1986",
      title: "Foundation & Vision",
      description: "Bel-Aire Mechanical was founded with a simple mission: to provide Arizona with the highest quality mechanical contracting services. Starting as a small team with big dreams, we focused on building lasting relationships and delivering exceptional craftsmanship.",
      icon: "foundation"
    },
    {
      year: "1990s", 
      title: "Rapid Expansion",
      description: "As Arizona's construction boom took off, so did we. We expanded our services, grew our team, and invested in cutting-edge technology. Our reputation for reliability and innovation spread throughout the Southwest.",
      icon: "trending_up"
    },
    {
      year: "2000s",
      title: "Manufacturing Excellence", 
      description: "We opened our state-of-the-art manufacturing facilities, bringing pipe, plumbing, and sheet metal fabrication in-house. This vertical integration allowed us to maintain the highest quality standards while reducing costs for our clients.",
      icon: "precision_manufacturing"
    },
    {
      year: "2010s",
      title: "Strategic Partnerships",
      description: "We forged key partnerships and expanded our capabilities in sustainable energy solutions. Our focus shifted toward helping clients reduce their carbon footprint while maintaining peak performance.",
      icon: "people"
    },
    {
      year: "2020s",
      title: "Acquisition",
      description: "Bel-Aire Mechanical was acquired by Legence in October of 2021, becoming part of the Legence family of companies.",
      icon: "handshake"
    },
    {
      year: "Today",
      title: "Arizona's Largest",
      description: "As part of the Legence family, we've become Arizona's largest mechanical contractor. We continue to lead the industry with innovative solutions, exceptional service, and an unwavering commitment to our clients' success.",
      icon: "star"
    }
  ];

  const nextHistorySlide = () => {
    setCurrentHistorySlide((prev) => (prev + 1) % historySlides.length);
  };

  const prevHistorySlide = () => {
    setCurrentHistorySlide((prev) => (prev - 1 + historySlides.length) % historySlides.length);
  };

  const goToHistorySlide = (index) => {
    setCurrentHistorySlide(index);
  };

  // Scroll-based text transition effect
  useEffect(() => {
    const handleScroll = () => {
      if (heroSectionRef.current) {
        const heroHeight = heroSectionRef.current.offsetHeight;
        const scrollY = window.scrollY;
        // Calculate progress from 0 to 1 as user scrolls through hero
        const progress = Math.min(Math.max(scrollY / (heroHeight * 0.6), 0), 1);
        setScrollProgress(progress);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  const startVideoTimeCheck = () => {
    if (checkIntervalRef.current) {
      clearInterval(checkIntervalRef.current);
    }
    
    checkIntervalRef.current = setInterval(() => {
      if (playerRef.current && playerRef.current.getCurrentTime) {
        try {
          const currentTime = playerRef.current.getCurrentTime();
          const playerState = playerRef.current.getPlayerState();
          
          // Check if video has looped (current time is much less than last time)
          if (lastTimeRef.current > 10 && currentTime < 5) {
            console.log('Video looped detected, resetting hero text');
            resetHeroText();
          }
          
          // Check if video is stuck or ended unexpectedly
          if (playerState === 0 || playerState === 5) { // 0 = ended, 5 = cued
            console.log('Video ended or stuck, attempting to restart');
            try {
              playerRef.current.seekTo(0);
              playerRef.current.playVideo();
              resetHeroText();
            } catch (restartError) {
              console.log('Error restarting video:', restartError);
            }
          }
          
          lastTimeRef.current = currentTime;
        } catch (error) {
          console.log('Error checking video time:', error);
        }
      }
    }, 2000); // Check every 2 seconds
  };
  
  useEffect(() => {
    // Load YouTube API
    const loadYouTubeAPI = () => {
      if (window.YT) {
        initializePlayer();
        return;
      }

      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

      window.onYouTubeIframeAPIReady = initializePlayer;
    };

    const initializePlayer = () => {
      try {
        const ytPlayer = new window.YT.Player('hero-video-player', {
          events: {
            onStateChange: onPlayerStateChange,
            onReady: onPlayerReady,
            onError: onPlayerError,
          },
        });
        setPlayer(ytPlayer);
        playerRef.current = ytPlayer;
      } catch (error) {
        console.error('Error initializing YouTube player:', error);
      }
    };

    const onPlayerReady = () => {
      console.log('YouTube player ready');
      try {
        // Ensure the video starts playing
        playerRef.current.playVideo();
        startVideoTimeCheck();
        startPeriodicVideoReset();
        setupIntersectionObserver();
      } catch (error) {
        console.log('Error starting video playback:', error);
      }
    };

    const setupIntersectionObserver = () => {
      if (!heroSectionRef.current) return;

      const options = {
        root: null,
        rootMargin: '0px',
        threshold: 0.3 // Trigger when 30% of the video is visible
      };

      intersectionObserverRef.current = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Video section is in view - restart video if needed
            console.log('Hero section in view, checking video state');
            if (playerRef.current) {
              try {
                const playerState = playerRef.current.getPlayerState();
                // If video is not playing (states: -1=unstarted, 0=ended, 2=paused, 5=cued)
                if (playerState !== 1) {
                  console.log('Restarting video on scroll back to top');
                  playerRef.current.seekTo(0);
                  playerRef.current.playVideo();
                  resetHeroText();
                }
              } catch (error) {
                console.log('Error checking/restarting video on intersection:', error);
              }
            }
          }
        });
      }, options);

      intersectionObserverRef.current.observe(heroSectionRef.current);
    };

    const startPeriodicVideoReset = () => {
      // Reset video every 5 minutes to prevent getting stuck
      if (videoResetIntervalRef.current) {
        clearInterval(videoResetIntervalRef.current);
      }
      
      videoResetIntervalRef.current = setInterval(() => {
        if (playerRef.current) {
          try {
            console.log('Periodic video reset');
            playerRef.current.seekTo(0);
            playerRef.current.playVideo();
            resetHeroText();
          } catch (error) {
            console.log('Error in periodic video reset:', error);
          }
        }
      }, 300000); // 5 minutes
    };

    const onPlayerError = (event) => {
      console.error('YouTube player error:', event.data);
      // Try to reload the video after an error
      setTimeout(() => {
        if (playerRef.current) {
          try {
            playerRef.current.seekTo(0);
            playerRef.current.playVideo();
          } catch (retryError) {
            console.log('Error retrying video:', retryError);
          }
        }
      }, 3000);
    };

    const onPlayerStateChange = (event) => {
      console.log('Player state changed:', event.data);
      // YT.PlayerState.ENDED = 0, YT.PlayerState.PLAYING = 1, YT.PlayerState.PAUSED = 2
      if (event.data === 0) {
        // Video ended, restart it manually
        console.log('Video ended, manually restarting');
        try {
          playerRef.current.seekTo(0);
          playerRef.current.playVideo();
          resetHeroText();
        } catch (error) {
          console.log('Error restarting video:', error);
        }
      } else if (event.data === 1) {
        // Video started playing
        console.log('Video started playing');
        if (!checkIntervalRef.current) {
          startVideoTimeCheck();
        }
      } else if (event.data === 2) {
        // Video paused - restart it
        console.log('Video paused, restarting');
        try {
          playerRef.current.playVideo();
        } catch (error) {
          console.log('Error resuming video:', error);
        }
      }
    };

    loadYouTubeAPI();
    
    // Start initial fade timer
    startFadeTimer();
    
    // Cleanup
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
      if (videoResetIntervalRef.current) {
        clearInterval(videoResetIntervalRef.current);
      }
      if (intersectionObserverRef.current) {
        intersectionObserverRef.current.disconnect();
      }
      if (playerRef.current) {
        try {
          playerRef.current.destroy();
        } catch (error) {
          console.log('Error destroying player:', error);
        }
      }
    };
  }, []);

  return (
    <>
      {/* Hero Section */}
      <section id="home" className="hero" ref={heroSectionRef}>
        <div className="hero-video">
          <iframe
            id="hero-video-player"
            className="hero-video-player"
            src="https://www.youtube.com/embed/0VDOTlfklk0?autoplay=1&mute=1&loop=1&playlist=0VDOTlfklk0&controls=0&showinfo=0&rel=0&iv_load_policy=3&modestbranding=1&disablekb=1&enablejsapi=1"
            title="Bel-Aire Mechanical - Arizona's Largest Mechanical Contractor"
            allow="autoplay; encrypted-media"
            allowFullScreen
          ></iframe>
        </div>
        <div className="hero-overlay">
          <div className="container">
            <div className="hero-content">
              <div 
                ref={heroTextRef}
                className="hero-text"
                style={{
                  transform: `translateY(${scrollProgress * 150}px)`,
                  opacity: isHeroTextVisible ? (1 - (scrollProgress * 0.8)) : 0,
                  transition: 'transform 0.1s ease-out, opacity 2s ease-out'
                }}
              >
                <h1>Arizona's Largest Mechanical Contractor</h1>
                <p>
                  Bel-Aire Mechanical is Arizona's Mechanical Contractor. We are centrally located in Phoenix, AZ. Our principal corporate office building and expanded pipe, plumbing and sheet metal manufacturing facilities are all state-of-the-art.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Hero Intro Section - Text lands here */}
      <section className="hero-intro" style={{
        background: 'linear-gradient(180deg, rgba(0, 104, 166, 0.95) 0%, rgba(0, 104, 166, 0.85) 50%, var(--white) 100%)',
        padding: '4rem 0 6rem',
        marginTop: '-2rem',
        position: 'relative',
        zIndex: 10
      }}>
        <div className="container">
          <div style={{
            maxWidth: '900px',
            margin: '0 auto',
            textAlign: 'center',
            opacity: Math.min(scrollProgress * 2, 1),
            transform: `translateY(${Math.max(30 - (scrollProgress * 60), 0)}px)`,
            transition: 'transform 0.2s ease-out, opacity 0.2s ease-out'
          }}>
            <h2 style={{
              color: 'var(--white)',
              fontSize: '2.5rem',
              fontWeight: '700',
              marginBottom: '1.5rem',
              textShadow: '2px 2px 4px rgba(0, 0, 0, 0.3)'
            }}>
              Arizona's Largest Mechanical Contractor
            </h2>
            <p style={{
              color: 'rgba(255, 255, 255, 0.95)',
              fontSize: '1.25rem',
              lineHeight: '1.8',
              textShadow: '1px 1px 3px rgba(0, 0, 0, 0.2)'
            }}>
              Bel-Aire Mechanical is Arizona's Mechanical Contractor. We are centrally located in Phoenix, AZ. Our principal corporate office building and expanded pipe, plumbing and sheet metal manufacturing facilities are all state-of-the-art.
            </p>
          </div>
        </div>
      </section>
      
      {/* Awards Section */}
      <section id="awards" className="awards">
        <div className="awards-container">
            <div className="awards-content">
          <img src="/images/Awards_2023-2024.png" alt="Ranking AZ 2023-2024 award" className="awards-img" />
            </div>
        </div>
      </section>

                   {/* Legence Section */}
      <section id="legence" className="section legence">
        <div className="legence-content">
             <div className="legence-text">
               <h2>We Are Legence</h2>
               <p className="legence-intro">
                 Bel-Aire Mechanical is a member of the Legence family of companies.
               </p>
               <p>
                 Legence, a Blackstone portfolio company, is an Energy Transition Accelerator™ that provides advisory services and implementation focused on financing, designing, building, and servicing complex systems in mission-critical and high performance facilities.
               </p>
               <p>
                 With five decades of expertise in the built environment, Legence has a proven track record of reducing carbon emissions, implementing renewables, lowering utility costs through efficiency consumption, and making systems run better at unmatched speed and scale.
               </p>
             </div>
                         <div className="legence-image">
              <img src="/images/Post-B_Vertical.png" alt="We Are Legence - Energy Transition Accelerator" className="legence-img" />
            </div>
        </div>
      </section>

      {/* About Us Section */}
      <section id="about-us" className="section about-us">
        <div className="container">
          <div className="section-header">
            <h2>Our History</h2>
            <p>
            Building Excellence in Arizona Since 1986
            </p>
          </div>
          
          <div className="history-carousel">
            <div className="carousel-container">
              <div className="carousel-track" style={{transform: `translateX(-${currentHistorySlide * 100}%)`}}>
                {historySlides.map((slide, index) => (
                  <div key={index} className="carousel-slide">
                    <div className="slide-icon">
                      <span className="material-icons">{slide.icon}</span>
                    </div>
                    <div className="slide-content">
                      <div className="slide-year">{slide.year}</div>
                      <h3>{slide.title}</h3>
                      <p>{slide.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="carousel-controls">
              <button className="carousel-btn carousel-prev" onClick={prevHistorySlide}>
                <span className="material-icons">chevron_left</span>
              </button>
              <button className="carousel-btn carousel-next" onClick={nextHistorySlide}>
                <span className="material-icons">chevron_right</span>
              </button>
            </div>
            
            <div className="carousel-indicators">
              {historySlides.map((_, index) => (
                <button
                  key={index}
                  className={`indicator ${index === currentHistorySlide ? 'active' : ''}`}
                  onClick={() => goToHistorySlide(index)}
                />
              ))}
            </div>
          </div>

          <div className="about-stats-showcase">
            <div className="stats-container">
              <div className="stat-item">
                <div className="stat-visual">
                  <div className="stat-circle">
                    <span className="stat-number">40</span>
                  </div>
                </div>
                <div className="stat-info">
                  <h4>Years of Excellence</h4>
                  <p>Four decades serving Arizona</p>
                </div>
              </div>
              
              <div className="stat-item">
                <div className="stat-visual">
                  <div className="stat-circle">
                    <span className="stat-number">1000+</span>
                  </div>
                </div>
                <div className="stat-info">
                  <h4>Projects Completed</h4>
                  <p>From small repairs to major installations</p>
                </div>
              </div>
              
              <div className="stat-item">
                <div className="stat-visual">
                  <div className="stat-circle">
                    <span className="stat-number">24/7</span>
                  </div>
                </div>
                <div className="stat-info">
                  <h4>Always Available</h4>
                  <p>Emergency service when you need it</p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* Departments Section */}
      <section id="departments" className="section departments">
        <div className="container">
          <div className="section-header">
            <h2>A Glance at Our Departments</h2>
            <p>
              Specialized teams delivering excellence across every aspect of mechanical contracting
            </p>
          </div>
          
          <div className="flip-cards-grid">
            {/* Safety Card */}
            <div 
              className={`flip-card ${flippedCards.has(0) ? 'flipped' : ''}`} 
              onClick={(e) => handleCardClick(0, e)}
              onTouchEnd={(e) => handleCardTouch(0, e)}
            >
              <div className="flip-card-inner">
                <div className="flip-card-front">
                  <div className="card-icon"><span className="material-icons">security</span></div>
                  <h3>Safety</h3>
                  <p>Protecting our team and clients with industry-leading safety protocols</p>
                </div>
                <div className="flip-card-back">
                  <h3>Safety Excellence</h3>
                  <p>Our comprehensive safety program ensures zero incidents through rigorous training, cutting-edge equipment, and unwavering commitment to safety standards.</p>
                  <a href="/safety" className="card-button">Learn More</a>
                </div>
              </div>
            </div>

            {/* Preconstruction Card */}
            <div 
              className={`flip-card ${flippedCards.has(1) ? 'flipped' : ''}`} 
              onClick={(e) => handleCardClick(1, e)}
              onTouchEnd={(e) => handleCardTouch(1, e)}
            >
              <div className="flip-card-inner">
                <div className="flip-card-front">
                  <div className="card-icon"><span className="material-icons">assignment</span></div>
                  <h3>Preconstruction</h3>
                  <p>Strategic planning and design services that set projects up for success</p>
                </div>
                <div className="flip-card-back">
                  <h3>Project Planning Excellence</h3>
                  <p>Comprehensive preconstruction services including cost estimation, value engineering, and constructability reviews to optimize project outcomes.</p>
                  <a href="/preconstruction" className="card-button">Learn More</a>
                </div>
              </div>
            </div>

            {/* BIM - VDC/Trimble Card */}
            <div 
              className={`flip-card ${flippedCards.has(2) ? 'flipped' : ''}`} 
              onClick={(e) => handleCardClick(2, e)}
              onTouchEnd={(e) => handleCardTouch(2, e)}
            >
              <div className="flip-card-inner">
                <div className="flip-card-front">
                  <div className="card-icon"><span className="material-icons">computer</span></div>
                  <h3>BIM - VDC</h3>
                  <p>Advanced 3D modeling and virtual design construction using Trimble technology</p>
                </div>
                <div className="flip-card-back">
                  <h3>Virtual Design & Construction</h3>
                  <p>State-of-the-art BIM and VDC services using Trimble technology for precise project visualization, clash detection, and seamless coordination.</p>
                  <a href="/bim-vdc" className="card-button">Explore BIM</a>
                </div>
              </div>
            </div>

            {/* Service Department Card */}
            <div 
              className={`flip-card ${flippedCards.has(3) ? 'flipped' : ''}`} 
              onClick={(e) => handleCardClick(3, e)}
              onTouchEnd={(e) => handleCardTouch(3, e)}
            >
              <div className="flip-card-inner">
                <div className="flip-card-front">
                  <div className="card-icon"><span className="material-icons">info</span></div>
                  <h3>Service Department</h3>
                  <p>24/7 maintenance and repair services to keep your systems running smoothly</p>
                </div>
                <div className="flip-card-back">
                  <h3>Always Available</h3>
                  <p>Round-the-clock service and maintenance solutions with emergency response, preventive maintenance programs, and expert technicians.</p>
                  <a href="/service-department" className="card-button">View Service</a>
                </div>
              </div>
            </div>

            {/* Technology & Innovation Card */}
            <div 
              className={`flip-card ${flippedCards.has(4) ? 'flipped' : ''}`} 
              onClick={(e) => handleCardClick(4, e)}
              onTouchEnd={(e) => handleCardTouch(4, e)}
            >
              <div className="flip-card-inner">
                <div className="flip-card-front">
                  <div className="card-icon"><span className="material-icons">lightbulb</span></div>
                  <h3>Technology & Innovation</h3>
                  <p>Cutting-edge solutions and innovative approaches to mechanical systems</p>
                </div>
                <div className="flip-card-back">
                  <h3>Leading Innovation</h3>
                  <p>Embracing the latest technologies and innovative methodologies to deliver superior mechanical solutions and drive industry advancement.</p>
                  <a href="/technology" className="card-button">Explore Tech</a>
                </div>
              </div>
            </div>

            {/* Construction through Commissioning Card */}
            <div 
              className={`flip-card ${flippedCards.has(5) ? 'flipped' : ''}`} 
              onClick={(e) => handleCardClick(5, e)}
              onTouchEnd={(e) => handleCardTouch(5, e)}
            >
              <div className="flip-card-inner">
                <div className="flip-card-front">
                  <div className="card-icon"><span className="material-icons">construction</span></div>
                  <h3>Construction & Commissioning</h3>
                  <p>Full-service construction from groundbreaking to system commissioning</p>
                </div>
                <div className="flip-card-back">
                  <h3>Complete Project Delivery</h3>
                  <p>End-to-end construction services including installation, testing, and commissioning to ensure optimal system performance and client satisfaction.</p>
                  <a href="/construction-commissioning" className="card-button">Read More</a>
                </div>
              </div>
            </div>

            {/* Manufacturing Card */}
            <div 
              className={`flip-card ${flippedCards.has(6) ? 'flipped' : ''}`} 
              onClick={(e) => handleCardClick(6, e)}
              onTouchEnd={(e) => handleCardTouch(6, e)}
            >
              <div className="flip-card-inner">
                <div className="flip-card-front">
                  <div className="card-icon"><span className="material-icons">precision_manufacturing</span></div>
                  <h3>Manufacturing</h3>
                  <p>State-of-the-art facilities for custom mechanical component fabrication</p>
                </div>
                <div className="flip-card-back">
                  <h3>Custom Fabrication</h3>
                  <p>Advanced manufacturing capabilities with quality control systems ensuring precision fabrication for all mechanical components and assemblies.</p>
                  <a href="/manufacturing" className="card-button">Learn More</a>
                </div>
              </div>
            </div>

            {/* Pipe Card */}
            <div 
              className={`flip-card ${flippedCards.has(7) ? 'flipped' : ''}`} 
              onClick={(e) => handleCardClick(7, e)}
              onTouchEnd={(e) => handleCardTouch(7, e)}
            >
              <div className="flip-card-inner">
                <div className="flip-card-front">
                  <div className="card-icon"><span className="material-icons">plumbing</span></div>
                  <h3>Pipe</h3>
                  <p>Precision pipe fabrication and installation for all mechanical systems</p>
                </div>
                <div className="flip-card-back">
                  <h3>Pipe Specialists</h3>
                  <p>Expert pipe fabrication, welding, and installation services using advanced techniques and materials for optimal system performance and longevity.</p>
                  <a href="/pipe" className="card-button">Learn More</a>
                </div>
              </div>
            </div>

            {/* Sheet Metal Card */}
            <div 
              className={`flip-card ${flippedCards.has(8) ? 'flipped' : ''}`} 
              onClick={(e) => handleCardClick(8, e)}
              onTouchEnd={(e) => handleCardTouch(8, e)}
            >
              <div className="flip-card-inner">
                <div className="flip-card-front">
                  <div className="card-icon"><span className="material-icons">handyman</span></div>
                  <h3>Sheet Metal</h3>
                  <p>Custom sheet metal fabrication for HVAC and mechanical applications</p>
                </div>
                <div className="flip-card-back">
                  <h3>Sheet Metal Experts</h3>
                  <p>Precision sheet metal fabrication and ductwork manufacturing using cutting-edge equipment for superior fit, finish, and performance.</p>
                  <a href="/sheet-metal" className="card-button">Learn More</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

    </>
  );
};

export default HomePage;
